import { useState, useMemo } from 'react';
import { themeColor } from './colorThemes';
import { Tooltip } from '../common/Tooltip';
import CompareEventsModal from './CompareEventsModal';

export const EVENT_TOOLTIPS = {
  page_view: 'Number of pages viewed.',
  user_engagement: 'Time users actively interacted with your site.',
  session_start: 'When a user starts a visit/session.',
  first_visit: 'New users visiting for the first time.',
  scroll: 'Users scrolling down the page.',
  form_start: 'Users started filling a form.',
  file_download: 'Files downloaded (PDFs, etc.).',
  click: 'Clicks on links/buttons.',
  form_submit: 'Completed form submissions.',
};

const FORM_EVENT_NAMES = new Set([
  'generate_lead',
  'form_submit',
  'form_start',
  'contact_form',
  'form_submission',
  'contact_form_submit',
  'wpforms_submit',
]);

const ENGAGEMENT_EVENTS = new Set([
  'page_view',
  'session_start',
  'first_visit',
  'scroll',
  'click',
  'user_engagement',
  'view_item',
]);

const HIDDEN_EVENTS = new Set(['view_search_results']);
// Redundant when form submissions come from the WordPress webhook (FORM_WEBHOOK_PROJECT_IDS).
const WEBHOOK_HIDDEN_FORM_EVENTS = new Set(['form_start', 'contact_form_submitted']);

function isHiddenEvent(eventName, formSubmissionsEnabled) {
  if (HIDDEN_EVENTS.has(eventName)) return true;
  if (formSubmissionsEnabled && WEBHOOK_HIDDEN_FORM_EVENTS.has(eventName)) return true;
  return false;
}

function formatNumber(n) {
  if (n == null) return '—';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString();
}

function eventBadge(eventName) {
  if (eventName === 'file_download') {
    return { label: 'Download', classes: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' };
  }
  if (FORM_EVENT_NAMES.has(eventName)) {
    return { label: 'Form', classes: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' };
  }
  if (ENGAGEMENT_EVENTS.has(eventName)) {
    return null;
  }
  if (eventName.startsWith('purchase') || eventName === 'add_to_cart' || eventName === 'begin_checkout') {
    return { label: 'Ecommerce', classes: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' };
  }
  return null;
}

export default function GA4EventsPanel({ events, themeKey, siteId, formSubmissionsEnabled = false }) {
  const [expanded, setExpanded] = useState(false);
  const [compareOpen, setCompareOpen] = useState(false);
  const list = Array.isArray(events) ? events : [];

  const { totalCount, maxCount, sorted } = useMemo(() => {
    const filtered = list.filter((e) => !isHiddenEvent(e.eventName, formSubmissionsEnabled));
    const total = filtered.reduce((sum, e) => sum + (e.eventCount || 0), 0);
    const max = filtered.reduce((m, e) => Math.max(m, e.eventCount || 0), 0);
    const s = [...filtered].sort((a, b) => (b.eventCount || 0) - (a.eventCount || 0));
    return { totalCount: total, maxCount: max, sorted: s };
  }, [list, formSubmissionsEnabled]);

  const visibleRows = expanded ? sorted : sorted.slice(0, 20);
  const hasMore = sorted.length > 20;

  return (
    <div>
      {/* Event and clicks */}
      <div className="flex items-start justify-between mb-4">
        <div>
          <h4 className="text-sm font-semibold text-brand-on-surface dark:text-brand-outline-variant flex items-center gap-2">
            <svg className="w-4 h-4 text-brand-outline" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            Event and clicks
          </h4>
          <p className="text-[11px] text-brand-on-surface-variant dark:text-brand-outline mt-0.5 font-label">
            Every event tracked during this period — use this to identify real event names in your GA4 setup.
          </p>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          {/* {sorted.length > 0 && (
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-wider text-brand-outline font-label">Total events</div>
              <div className="text-lg font-bold tabular-nums text-brand-on-surface dark:text-white font-headline">{formatNumber(totalCount)}</div>
            </div>
          )} */}
          {siteId && sorted.length > 0 && (
            <button
              type="button"
              onClick={() => setCompareOpen(true)}
              className="text-xs font-medium px-3 py-1.5 rounded-lg border border-brand-outline-variant dark:border-brand-outline text-brand-on-surface-variant dark:text-brand-outline hover:bg-brand-surface-container-low dark:hover:bg-brand-on-surface transition-colors flex items-center gap-1.5 font-label"
              title="Compare against a past period"
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 21L3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5" />
              </svg>
              Compare
            </button>
          )}
        </div>
      </div>

      {sorted.length === 0 ? (
        <div className="text-center py-10 rounded-lg border border-dashed border-brand-outline-variant dark:border-brand-outline">
          <p className="text-sm text-brand-on-surface-variant dark:text-brand-outline">No events tracked in this period.</p>
          <p className="text-[11px] text-brand-outline dark:text-brand-on-surface-variant mt-1 font-label">Ensure GA4 tag is installed and firing on your site.</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-brand-outline-variant dark:border-brand-outline">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-brand-surface-container-low dark:bg-brand-on-surface/50">
                <th className="text-left py-2.5 px-3 font-medium text-brand-on-surface-variant dark:text-brand-outline text-xs uppercase tracking-wider w-8 font-label">#</th>
                <th className="text-left py-2.5 px-3 font-medium text-brand-on-surface-variant dark:text-brand-outline text-xs uppercase tracking-wider font-label">Event Name</th>
                <th className="text-right py-2.5 px-3 font-medium text-brand-on-surface-variant dark:text-brand-outline text-xs uppercase tracking-wider font-label">Count</th>
                <th className="text-right py-2.5 px-3 font-medium text-brand-on-surface-variant dark:text-brand-outline text-xs uppercase tracking-wider hidden sm:table-cell font-label">Users</th>
                <th className="text-left py-2.5 px-3 font-medium text-brand-on-surface-variant dark:text-brand-outline text-xs uppercase tracking-wider hidden md:table-cell font-label">Share</th>
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((e, i) => {
                const badge = eventBadge(e.eventName);
                const pct = maxCount > 0 ? (e.eventCount / maxCount) * 100 : 0;
                const sharePct = totalCount > 0 ? ((e.eventCount / totalCount) * 100).toFixed(1) : '0.0';
                const barColor = themeColor(themeKey, i % 6);
                return (
                  <tr key={e.eventName} className="border-t border-gray-50 dark:border-brand-outline hover:bg-brand-surface-container-low dark:hover:bg-brand-on-surface/30 transition-colors">
                    <td className="py-2 px-3 text-brand-outline dark:text-brand-on-surface-variant tabular-nums">{i + 1}</td>
                    <td className="py-2 px-3 max-w-[260px]">
                      <div className="flex items-center gap-2">
                        {EVENT_TOOLTIPS[e.eventName] ? (
                          <Tooltip content={EVENT_TOOLTIPS[e.eventName]} placement="top">
                            <span className="text-xs text-brand-on-surface dark:text-brand-outline-variant truncate font-label cursor-help underline decoration-dotted underline-offset-2 decoration-brand-outline/60">
                              {e.eventName}
                            </span>
                          </Tooltip>
                        ) : (
                          <span className="text-xs text-brand-on-surface dark:text-brand-outline-variant truncate font-label" title={e.eventName}>
                            {e.eventName}
                          </span>
                        )}
                        {badge && (
                          <span className={`text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded flex-shrink-0 ${badge.classes} font-label`}>
                            {badge.label}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="py-2 px-3 text-right tabular-nums font-semibold text-brand-on-surface dark:text-white">
                      {formatNumber(e.eventCount)}
                    </td>
                    <td className="py-2 px-3 text-right tabular-nums text-brand-on-surface-variant dark:text-brand-outline hidden sm:table-cell">
                      {formatNumber(e.totalUsers)}
                    </td>
                    <td className="py-2 px-3 hidden md:table-cell">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-1.5 bg-brand-surface-container-high dark:bg-brand-on-surface rounded-full overflow-hidden min-w-[80px]">
                          <div
                            className="h-full rounded-full transition-all duration-500"
                            style={{ width: `${Math.max(pct, 2)}%`, backgroundColor: barColor }}
                          />
                        </div>
                        <span className="text-[10px] text-brand-outline dark:text-brand-on-surface-variant tabular-nums w-10 text-right font-label">
                          {sharePct}%
                        </span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {hasMore && (
        <div className="flex justify-center mt-3">
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="text-xs font-medium text-brand-primary dark:text-brand-400 hover:underline font-label"
          >
            {expanded ? `Show top 20 only` : `Show all ${sorted.length} events`}
          </button>
        </div>
      )}

      {siteId && (
        <CompareEventsModal
          isOpen={compareOpen}
          onClose={() => setCompareOpen(false)}
          siteId={siteId}
          currentEvents={list}
          currentLabel="Current Period"
          formSubmissionsEnabled={formSubmissionsEnabled}
        />
      )}
    </div>
  );
}
